<template>
  <div>
    <v-card flat color="#fffbf1" class="pa-4 brownBorder">
      <h3>Plaquenil Cumulative Dose Calculator</h3>
      <hr class="mb-6" />
      <!-- <v-divider></v-divider> -->
      <div class="d-sm-flex flex-sm-row flex-wrap align-start">
        <div style="width: 500px; max-width: 100%" class="mr-4">
          <div
            v-for="(cumRow, index) in cumRows"
            :key="index"
            class="d-flex flex-row align-center justify-space-between mb-2"
          >
            <div class="shortInput">
              <v-text-field
                v-model="cumRow.dose"
                type="number"
                outlined
                dense
                :hide-details="true"
                class="whiteInput mx-1 mx-sm-0"
              />
            </div>
            <div class="d-none d-sm-flex">mg</div>
            <div class="medInput">
              <v-select
                v-model="cumRow.sig"
                :items="doseOptions"
                item-text="text"
                item-value="val"
                :hide-details="true"
                outlined
                dense
                class="whiteInput mx-1 mx-sm-0"
              >
              </v-select>
            </div>
            <div class="d-none d-sm-flex">for</div>
            <div class="shortInput">
              <v-text-field
                v-model="cumRow.interval"
                type="number"
                outlined
                dense
                :hide-details="true"
                class="whiteInput mx-1 mx-sm-0"
              />
            </div>
            <div class="medInput">
              <v-select
                v-model="cumRow.intervalUnit"
                :items="intervalOptions"
                item-text="text"
                item-value="val"
                :hide-details="true"
                outlined
                dense
                class="whiteInput mx-1 mx-sm-0"
              >
              </v-select>
            </div>
            <div style="min-width: 40px">
              <v-fade-transition>
                <button
                  icon
                  @click="removeCumRow(index)"
                  v-if="cumRows.length > 1"
                >
                  <v-icon color="red">mdi-minus-circle</v-icon>
                </button></v-fade-transition
              >
            </div>
          </div>
          <div class="my-3 d-flex flex-row">
            <v-spacer></v-spacer>
            <v-fade-transition>
              <v-btn text small color="green" @click="addCumRow()" class="">
                <v-icon small color="green">mdi-plus-circle</v-icon> Add Row
              </v-btn>
            </v-fade-transition>
            <div style="width: 40px"></div>
          </div>
          <div class="d-flex justify-center align-center">
            <div>
              <b class="mr-3">Cumulative Dose</b>
              <span
                class="embiggen"
                :class="cumulativeDose >= 1000 ? 'red--text' : 'green--text'"
                >{{ cumulativeDose }}g</span
              >
            </div>
          </div>
          <div class="caption mt-6 mb-2">
            The majority of cases of retinotoxicity have occurred in patients
            that have had a cumulative dose exceeding 1000g of
            hydroxychloriquine (Plaquenil). <br /><br />This level is reached in
            about 7 years with the most common daily dose of Plaquenil, 400
            mg/day (200 bid).
          </div>
          <!-- <v-btn @click="doCalc" small color="primary" depressed>Calculate</v-btn> -->
        </div>

        <!-- <div class="my-6"></div> -->
        <div class="brownBorder flex-grow pa-2">
          <chart
            class="mt-10 mt-sm-1"
            type="line"
            :options="chartOptions"
            :series="series"
            :width="$vuetify.breakpoint.smAndUp ? 500 : '95%'"
          ></chart>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import Chart from "vue-apexcharts";

export default {
  name: "PlaquenilCumulativeCalc",
  components: { Chart },
  props: {},
  data: function () {
    return {
      cumulativeDose: 0,
      cumRows: [],
      doseOptions: [
        { val: 1, text: "qd" },
        { val: 2, text: "bid" },
        // { val: 0.5, text: "qod" },
      ],
      intervalOptions: [
        { val: 12, text: "years" },
        { val: 1, text: "months" },
      ],
      chartOptions: {
        chart: {
          id: "cumulative-dose",
          background: "#fff",
          toolbar: {
            show: true,
            offsetX: 0,
            offsetY: 0,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false,
            },
          },
        },
        xaxis: {
          name: "Time",
          type: "numeric",
          stepSize: 1,
          tickAmount: "dataPoints",
          decimalsInFloat: 0,
          title: { text: "Months" },
          show: true,
          position: "bottom",
        },
        yaxis: {
          show: true,
          decimalsInFloat: 0,
          title: { text: "Cumulative Dose (g)" },
        },
        annotations: {
          yaxis: [
            {
              y: 1000,
              y2: 9000,
              borderColor: "red",
              fillColor: "pink",
            },
          ],
        },

        tooltip: {
          x: {
            formatter: function (value) {
              let rtn = "";
              if (value < 1) {
                rtn = value * 12 + " months";
              } else {
                let yearInt = Math.floor(value);
                rtn = yearInt + " year";
                if (yearInt > 1) rtn += "s";
                if (value % 1 !== 0)
                  rtn += " " + Math.round((value - yearInt) * 12) + " months";
              }

              return rtn;
            },
          },
        },
      },
      series: [
        {
          name: "Cumulative Dose (g)",
          data: [],
          show: true,
        },
      ],
    };
  },
  methods: {
    addCumRow() {
      this.cumRows.push({
        dose: 200,
        sig: 2,
        interval: 1,
        intervalUnit: 12,
      });
    },
    removeCumRow(index) {
      this.cumRows.splice(index, 1);
    },
    doCalc() {
      // let months = [];
      // let cumDose = [];
      let data = [{ x: 0, y: 0 }];
      let cumDose = 0;
      let months = 0;
      this.cumRows.forEach((row) => {
        let monthsToAdd = row.interval * row.intervalUnit;
        for (let i = 0; i < monthsToAdd; i++) {
          months++;

          let dose = (row.dose * row.sig * 365) / 12 / 1000;
          cumDose = cumDose + dose;
          // console.log(months, dose, cumDose);
          // cumDose.push(dose.toFixed(2));
          data.push({ x: months / 12, y: cumDose.toFixed(1) });
        }
      });
      this.cumulativeDose = Math.round(cumDose);
      this.updateChart(data);
    },

    updateChart(data) {
      // console.log(data);
      let xAxis = {};
      if (data.length < 14) {
        xAxis = {
          type: "numeric",
          stepSize: 1,
          tickAmount: "dataPoints",
          decimalsInFloat: 0,
        };
      } else {
        xAxis = {
          type: "numeric",
          tickAmount: (data.length - 1) / 12,
          stepSize: 1,
          decimalsInFloat: 0,

          title: { text: "Years" },
        };
      }
      this.chartOptions = {
        ...this.chartOptions,
        ...{
          xaxis: xAxis,
        },
      };
      this.series = [
        {
          name: "Cumulative Dose (g)",
          data: data,
        },
      ];
    },
  },
  computed: {},
  watch: {
    cumRows: {
      handler: function () {
        this.doCalc();
      },
      deep: true,
    },
  },
  mounted() {
    this.addCumRow();
    this.doCalc();
  },
};
</script>

<style scoped>
.shortInput {
  width: 65px;
}
.medInput {
  width: 100px;
}
.brownBorder {
  border: 1px solid #a79e89 !important;
  border-radius: 5px;
  background-color: #fff;
}
</style>
